import { useState, useEffect, useCallback } from 'react'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface HolidayItem {
  holiday_id: number
  holiday_date: string
  reason: string | null
}

interface HolidayListResponse {
  year_month: string
  closed_weekdays: number[]
  holidays: HolidayItem[]
  closed_dates: string[]
}

interface PublicHolidayItem {
  holiday_date: string
  name: string
}

interface HolidaySetting {
  auto_public_holiday: boolean
  last_synced_year: number | null
  last_synced_at: string | null
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']

// 0=일 ~ 6=토 값을 월~일 순서로 정렬
const sortWeekdays = (weekdays: number[]) =>
  [...weekdays].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))

const pad2 = (n: number) => String(n).padStart(2, '0')
const toDateString = (year: number, month: number, day: number) =>
  `${year}-${pad2(month)}-${pad2(day)}`

export default function HolidayTab() {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)

  const [closedWeekdays, setClosedWeekdays] = useState<number[]>([])
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([])
  const [holidays, setHolidays] = useState<HolidayItem[]>([])
  const [publicHolidays, setPublicHolidays] = useState<PublicHolidayItem[]>([])
  const [setting, setSetting] = useState<HolidaySetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settingSaving, setSettingSaving] = useState(false)

  const fetchMonth = useCallback(async () => {
    setLoading(true)
    try {
      const yearMonth = `${year}${pad2(month)}`
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/holiday?year_month=${yearMonth}`))
      if (!res.ok) throw new Error('조회 실패')
      const data: HolidayListResponse = await res.json()
      setClosedWeekdays(data.closed_weekdays)
      setSelectedWeekdays(data.closed_weekdays)
      setHolidays(data.holidays)
    } catch {
      alert('휴무일 정보를 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }

    // 공휴일 표시는 부가 정보 — 실패해도 달력은 정상 동작한다
    try {
      const yearMonth = `${year}${pad2(month)}`
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/holiday/admin/public?year_month=${yearMonth}`))
      if (!res.ok) throw new Error('조회 실패')
      const data: { public_holidays: PublicHolidayItem[] } = await res.json()
      setPublicHolidays(data.public_holidays)
    } catch {
      setPublicHolidays([])
    }
  }, [year, month])

  const fetchSetting = useCallback(async () => {
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/holiday/admin/setting'))
      if (!res.ok) throw new Error('조회 실패')
      const data: HolidaySetting = await res.json()
      setSetting(data)
    } catch {
      setSetting(null)
    }
  }, [])

  useEffect(() => {
    fetchMonth()
  }, [fetchMonth])

  useEffect(() => {
    fetchSetting()
  }, [fetchSetting])

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12) }
    else setMonth(m => m - 1)
  }
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1) }
    else setMonth(m => m + 1)
  }

  const toggleWeekday = (weekday: number) => {
    setSelectedWeekdays(prev =>
      prev.includes(weekday) ? prev.filter(w => w !== weekday) : [...prev, weekday].sort()
    )
  }

  const handleSaveWeekdays = async () => {
    if (!window.confirm('휴무요일을 저장하시겠습니까?')) return
    setSaving(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/holiday/admin/weekdays'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ closed_weekdays: selectedWeekdays }),
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '저장 실패') }
      const result = await res.json()
      alert(result.message ?? '저장되었습니다.')
      fetchMonth()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleAutoPublicHoliday = async () => {
    const nextValue = !(setting?.auto_public_holiday ?? false)
    const confirmMessage = nextValue
      ? '공휴일(대체공휴일 포함)을 모두 휴무일로 등록할까요?\n이전에 삭제하신 공휴일도 다시 등록됩니다.'
      : '자동 등록된 공휴일을 휴무일에서 모두 제외할까요?\n직접 등록하신 휴무일은 유지됩니다.'
    if (!window.confirm(confirmMessage)) return

    setSettingSaving(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/holiday/admin/setting'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_public_holiday: nextValue }),
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '저장 실패') }
      const result = await res.json()
      alert(result.message ?? '저장되었습니다.')
      fetchSetting()
      fetchMonth()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '저장에 실패했습니다.')
    } finally {
      setSettingSaving(false)
    }
  }

  const handleResyncPublicHolidays = async () => {
    if (!window.confirm(`${year}년 공휴일을 다시 불러올까요?\n삭제하신 공휴일도 다시 등록됩니다.`)) return

    setSettingSaving(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/holiday/admin/sync'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year }),
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '동기화 실패') }
      const result = await res.json()
      alert(result.message ?? '동기화되었습니다.')
      fetchSetting()
      fetchMonth()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '동기화에 실패했습니다.')
    } finally {
      setSettingSaving(false)
    }
  }

  const handleAddHoliday = async (day: number, publicHolidayName?: string) => {
    const holidayDate = toDateString(year, month, day)
    const label = publicHolidayName ? `${day}일(${publicHolidayName})` : `${day}일`
    if (!window.confirm(`${year}년 ${month}월 ${label}을 휴무일로 등록할까요?`)) return
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/holiday/admin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ holiday_date: holidayDate, reason: publicHolidayName ?? null }),
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '등록 실패') }
      fetchMonth()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '등록에 실패했습니다.')
    }
  }

  const handleDeleteHoliday = async (holiday: HolidayItem, day: number) => {
    if (!window.confirm(`${year}년 ${month}월 ${day}일 휴무일을 삭제할까요?`)) return
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/holiday/admin/${holiday.holiday_id}`), {
        method: 'DELETE',
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '삭제 실패') }
      fetchMonth()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '삭제에 실패했습니다.')
    }
  }

  const firstDay = new Date(year, month - 1, 1).getDay()
  const daysInMonth = new Date(year, month, 0).getDate()
  const calendarCells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]
  const holidayMap = new Map(holidays.map(h => [h.holiday_date, h]))
  const publicHolidayMap = new Map(publicHolidays.map(p => [p.holiday_date, p.name]))
  const autoPublicHoliday = setting?.auto_public_holiday ?? false

  return (
    <div className="flex flex-col gap-5 max-w-[720px]">

      {/* 휴무요일 설정 */}
      <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
        <h2 className="text-[14px] font-bold text-[#5a3d32] mb-1">휴무요일 설정</h2>
        <p className="text-[12px] text-gray-400 mb-6">
          매주 반복되는 휴무 요일을 설정합니다. 선택한 요일은 달력에 자동으로 휴무 처리됩니다.
        </p>

        <div className="flex gap-2 mb-6">
          {WEEKDAYS.map((label, weekday) => {
            const selected = selectedWeekdays.includes(weekday)
            return (
              <button
                key={label}
                onClick={() => toggleWeekday(weekday)}
                className={[
                  'w-11 h-11 rounded-full text-[13px] font-semibold cursor-pointer transition-colors',
                  selected
                    ? 'bg-[#887267] text-white border-none'
                    : `bg-white border border-[#e8e0dc] hover:bg-[#f5f2f0] ${
                        weekday === 0 ? 'text-red-400' : weekday === 6 ? 'text-blue-400' : 'text-[#5a3d32]'
                      }`,
                ].join(' ')}
              >
                {label}
              </button>
            )
          })}

          <span className="w-px h-11 bg-[#e8e0dc] mx-2" />

          <button
            onClick={handleToggleAutoPublicHoliday}
            disabled={settingSaving}
            title="공휴일(대체공휴일 포함)을 모두 휴무일로 등록하거나 제외합니다"
            className={[
              'h-11 px-4 rounded-full text-[13px] font-semibold cursor-pointer transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              autoPublicHoliday
                ? 'bg-[#887267] text-white border-none'
                : 'bg-white border border-[#e8e0dc] text-[#5a3d32] hover:bg-[#f5f2f0]',
            ].join(' ')}
          >
            {autoPublicHoliday ? '✓ ' : ''}공휴일
          </button>
        </div>

        <p className="text-[11px] text-gray-400 -mt-3 mb-6">
          공휴일 버튼을 켜면 대체공휴일까지 모두 휴무일로 등록됩니다. 등록 후 특정 날짜만 달력에서 클릭해 제외할 수 있습니다.
        </p>

        <div className="border-t border-[#f5f2f0] pt-5 flex items-center justify-between">
          <p className="text-[11px] text-gray-400 m-0">
            현재 설정:{' '}
            <span className="text-[#887267] font-semibold">
              {closedWeekdays.length > 0 || autoPublicHoliday
                ? `${[
                    closedWeekdays.length > 0
                      ? `매주 ${sortWeekdays(closedWeekdays).map(w => WEEKDAYS[w]).join('·')}요일`
                      : null,
                    autoPublicHoliday ? '공휴일' : null,
                  ].filter(Boolean).join(', ')} 휴무`
                : '휴무요일 없음'}
            </span>
          </p>
          <button
            onClick={handleSaveWeekdays}
            disabled={saving}
            className="bg-[#887267] text-white text-[13px] font-semibold px-6 py-2.5 rounded-lg cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-none"
          >
            {saving ? '저장 중...' : '저장하기'}
          </button>
        </div>
      </div>

      {/* 휴무일 달력 */}
      <div className="bg-white rounded-2xl border border-[#e8e0dc] shadow-[0_2px_12px_rgba(136,114,103,0.06)] overflow-hidden">
        <div className="px-6 py-5 border-b border-[#f5f2f0]">
          <h2 className="text-[14px] font-bold text-[#5a3d32] mb-1">휴무일 관리</h2>
          <p className="text-[12px] text-gray-400 m-0">
            달력에서 날짜를 클릭해 휴무일을 추가하거나 삭제할 수 있습니다.
          </p>
        </div>

        <div className="flex items-center justify-center gap-4 px-6 py-4 border-b border-[#f5f2f0]">
          <button onClick={prevMonth} className="w-8 h-8 rounded-full hover:bg-[#f5f0ec] flex items-center justify-center transition-colors border-none bg-transparent cursor-pointer">
            <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#887267" strokeWidth={2}><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <span className="text-[15px] font-bold text-[#2a1f1a] w-[120px] text-center">
            {year}년 {month}월
          </span>
          <button onClick={nextMonth} className="w-8 h-8 rounded-full hover:bg-[#f5f0ec] flex items-center justify-center transition-colors border-none bg-transparent cursor-pointer">
            <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#887267" strokeWidth={2}><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="grid grid-cols-7 mb-2">
            {WEEKDAYS.map((d, i) => (
              <div key={d} className={`text-center text-[12px] font-semibold py-2 ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400'}`}>
                {d}
              </div>
            ))}
          </div>

          {loading ? (
            <div className="py-8 text-center text-[14px] text-gray-400">불러오는 중...</div>
          ) : (
            <div className="grid grid-cols-7 gap-y-1">
              {calendarCells.map((day, idx) => {
                if (!day) return <div key={`empty-${idx}`} />
                const dateString = toDateString(year, month, day)
                const holiday = holidayMap.get(dateString)
                const publicHolidayName = publicHolidayMap.get(dateString)
                const isWeekdayClosed = closedWeekdays.includes(idx % 7)
                const isToday =
                  year === now.getFullYear() && month === now.getMonth() + 1 && day === now.getDate()

                return (
                  <div key={dateString} className="flex flex-col items-center py-1">
                    <button
                      onClick={() => {
                        if (isWeekdayClosed) return
                        if (holiday) handleDeleteHoliday(holiday, day)
                        else handleAddHoliday(day, publicHolidayName)
                      }}
                      title={holiday?.reason ?? publicHolidayName ?? (isWeekdayClosed ? '휴무요일' : '')}
                      className={[
                        'w-9 h-9 rounded-full text-[13px] font-medium transition-colors border-none',
                        isWeekdayClosed
                          ? 'bg-[#f5f2f0] text-gray-300 cursor-not-allowed'
                          : holiday
                            ? 'bg-[#887267] text-white cursor-pointer hover:bg-[#6b5248]'
                            : `bg-transparent cursor-pointer hover:bg-[#f5f0ec] ${
                                publicHolidayName
                                  ? 'text-red-500 font-bold'
                                  : isToday
                                    ? 'ring-1 ring-[#887267] text-[#887267] font-bold'
                                    : 'text-[#2a1f1a]'
                              }`,
                      ].join(' ')}
                    >
                      {day}
                    </button>
                    <span className="h-[14px] mt-[2px] text-[10px] leading-[14px] text-red-400 truncate max-w-[56px]">
                      {publicHolidayName ?? ''}
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          <div className="flex items-center justify-between gap-4 mt-5 pt-4 border-t border-[#f5f2f0]">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-2 text-[11px] text-gray-400">
                <span className="w-3 h-3 rounded-full bg-[#f5f2f0] border border-[#e8e0dc] inline-block" />
                휴무요일
              </span>
              <span className="flex items-center gap-2 text-[11px] text-gray-400">
                <span className="w-3 h-3 rounded-full bg-[#887267] inline-block" />
                휴무일
              </span>
              <span className="flex items-center gap-2 text-[11px] text-gray-400">
                <span className="text-red-500 font-bold text-[11px]">15</span>
                공휴일
              </span>
            </div>
            <button
              onClick={handleResyncPublicHolidays}
              disabled={settingSaving}
              className="text-[11px] text-[#887267] font-semibold bg-transparent border border-[#e8e0dc] rounded-lg px-3 py-1.5 cursor-pointer hover:bg-[#f5f2f0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              공휴일 다시 불러오기
            </button>
          </div>

          {setting?.last_synced_at && (
            <p className="text-[11px] text-gray-400 mt-2 mb-0">
              마지막 공휴일 동기화:{' '}
              <span className="text-[#887267] font-semibold">
                {new Date(setting.last_synced_at).toLocaleString('ko-KR')}
              </span>
              {setting.last_synced_year && ` (${setting.last_synced_year}년까지)`}
            </p>
          )}

          <div className="mt-4">
            <p className="text-[12px] font-semibold text-[#5a3d32] mb-2">{month}월 휴무일</p>
            {holidays.length === 0 ? (
              <p className="text-[12px] text-gray-400 m-0">등록된 휴무일이 없습니다.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {holidays.map(h => (
                  <span
                    key={h.holiday_id}
                    className="text-[12px] text-[#5a3d32] bg-[#f5f2f0] rounded-lg px-3 py-1.5"
                  >
                    {h.holiday_date}
                    {h.reason && <span className="text-gray-400 ml-1">({h.reason})</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  )
}
